import React, { useEffect, useState } from 'react';
import { social as socialAPI, promos as promosAPI } from '../api';
import { B } from '../constants';

// Owner-only: publish the actual video to connected Facebook Pages, Instagram Reels and X.
const STATS_NAME = { instagram: 'insta_reel', facebook: 'fb_reel', x: 'twitter' };

export default function SocialPublishPanel({ promo, toast }) {
  const [info, setInfo] = useState(null);
  const [selected, setSelected] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    socialAPI.accounts().then(setInfo).catch(() => setInfo({ configured: {}, accounts: [] }));
  }, []);

  if (!info) return null;
  const metaAcct = info.accounts?.find(a => a.provider === 'meta');
  const xAcct = info.accounts?.find(a => a.provider === 'x');
  const targets = [
    ...(metaAcct?.pages || []).flatMap(p => [
      { key: `facebook:${p.id}`, platform: 'facebook', pageId: p.id, label: `Facebook · ${p.name}` },
      ...(p.hasInstagram ? [{ key: `instagram:${p.id}`, platform: 'instagram', pageId: p.id, label: `Instagram Reels · @${p.igUsername}` }] : []),
    ]),
    ...(xAcct ? [{ key: 'x', platform: 'x', label: `X · ${xAcct.displayName || 'connected'}` }] : []),
  ];

  const connect = async (provider) => {
    try {
      const { url } = await socialAPI.connect(provider);
      window.location.assign(url);
    } catch (err) {
      toast(err.message || 'Could not start connection');
    }
  };

  const publish = async () => {
    const chosen = targets.filter(t => selected[t.key]).map(({ platform, pageId }) => ({ platform, pageId }));
    if (!chosen.length) return;
    setPublishing(true);
    setResults(null);
    try {
      const data = await socialAPI.publish(promo.id, chosen);
      setResults(data.results || []);
      (data.results || []).filter(r => r.ok).forEach(r => {
        promosAPI.trackShareStructured(promo.id, STATS_NAME[r.platform]).catch(() => {});
      });
    } catch (err) {
      setResults(err.data?.results || [{ platform: 'all', ok: false, error: err.message || 'Publishing failed' }]);
    } finally {
      setPublishing(false);
    }
  };

  const btn = { padding: '7px 12px', borderRadius: 50, border: `1px solid ${B.forest}`, background: 'transparent', color: B.forest, fontSize: 12, fontWeight: 700, cursor: 'pointer' };

  return (
    <div data-testid="social-publish" style={{ border: '1px solid #e5e0d8', borderRadius: 14, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Publish to your accounts</div>
      <div style={{ fontSize: 12, color: B.muted, marginBottom: 10 }}>Posts the video itself, not just a link. Instagram needs a Business/Creator account linked to a Facebook Page.</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: targets.length ? 10 : 0 }}>
        {info.configured?.meta && <button type="button" style={btn} onClick={() => connect('meta')}>{metaAcct ? 'Reconnect Facebook/Instagram' : 'Connect Facebook/Instagram'}</button>}
        {info.configured?.x && <button type="button" style={btn} onClick={() => connect('x')}>{xAcct ? 'Reconnect X' : 'Connect X'}</button>}
        {!info.configured?.meta && !info.configured?.x && <span style={{ fontSize: 12, color: B.muted }}>Social publishing isn't set up on the server yet.</span>}
      </div>
      {targets.map(t => (
        <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 0' }}>
          <input type="checkbox" checked={!!selected[t.key]} onChange={e => setSelected(s => ({ ...s, [t.key]: e.target.checked }))} />
          {t.label}
        </label>
      ))}
      {targets.length > 0 && (
        <button type="button" disabled={publishing || !targets.some(t => selected[t.key])} onClick={publish}
          style={{ marginTop: 8, width: '100%', padding: 10, borderRadius: 50, border: 'none', background: B.forest, color: 'white', fontWeight: 700, cursor: 'pointer', opacity: publishing ? 0.7 : 1 }}>
          {publishing ? 'Publishing… (video processing can take a minute)' : 'Publish video'}
        </button>
      )}
      {results && results.map((r, i) => (
        <div key={i} style={{ fontSize: 12, marginTop: 6, color: r.ok ? B.forest : '#d64545' }}>
          {r.ok ? `✓ Posted to ${r.platform}` : `✕ ${r.platform}: ${r.error}`}
        </div>
      ))}
    </div>
  );
}
